// file: pages/Admin/UserManagement.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import {
  Flex, Box, Text, Badge,
} from "@chakra-ui/react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import {
  EllipsisVertical, Plus, Pencil, Contact, Archive, ShieldAlert, MessageSquare,
} from "lucide-react";
import GradientButton from "components/Buttons/GradientButton";
import AddUserDrawer from "./components/AddUserDrawer";
import ViewProfile from "./components/ViewProfile";
import EditUserDrawer from "./components/EditUserDrawer";
import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ArchiveUser from "./components/ArchiveUser";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsersAction, registerUserAction, updateUserAction } from '../../../state/actions/userActions';
import { deleteAccount, confirmAdminPassword } from "state/actions/authActions";
import { formatDate } from "utils/dateFormatter";
import { useCustomNotification } from 'components/Toasts/OpenNotification.js';
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import ManageDeletionModal from "./components/ManageDeletionModal";

const UserManagement = () => {
  const [isAddUserDrawerOpen, setAddUserDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewProfileDrawerOpen, setViewProfileDrawerOpen] = useState(false);
  const [isEditUserDrawerOpen, setEditUserDrawerOpen] = useState(false);
  const [isArchiveUserDialogOpen, setArchiveUserDialogOpen] = useState(false);
  const [selectedUserForArchive, setSelectedUserForArchive] = useState(null);
  const [isManageDeletionOpen, setManageDeletionOpen] = useState(false);
  const [selectedUserForDeletion, setSelectedUserForDeletion] = useState(null);
  const history = useHistory();
  const showToast = useCustomNotification();
  const dispatch = useDispatch();

  const { users: reduxUsers, loading, error, page, hasMore } = useSelector((state) => state.users);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    setUsers(reduxUsers || []);
  }, [reduxUsers]);


  const loadUsers = useCallback(async () => {
    try {
      await dispatch(fetchUsersAction());
    } catch (error) {
      console.error("loadUsers dispatch error:", error);
    }
  }, [dispatch]);


  useEffect(() => {
    loadUsers();
  }, [loadUsers]);




  const handleAddUser = async (newUser) => {
    const response = await dispatch(registerUserAction(newUser));

    if (response.success) {
      showToast({ type: "success", message: "Admin added successfully" });
      handleCloseAddUserDrawer();
    } else {
      showToast({
        type: "error",
        message: "Registration Failed",
        description: response.error.split('\n').map(msg => ` ${msg}`).join('\n'),
      });
    }
  };

  const handleUpdateUser = async (updatedUser) => {
    try {
      await dispatch(updateUserAction(updatedUser.user_id, updatedUser));

      showToast({
        type: "success",
        message: "User Updated",
        description: "User details updated successfully.",
      });

      setEditUserDrawerOpen(false);

    } catch (error) {
      showToast({
        type: "error",
        message: "Update Failed",
        description: error.message || "Failed to update user",
      });
    }
  };

  const handleDeleteAccount = async (userId) => {
    try {
      const response = await dispatch(deleteAccount(userId));

      if (response.success) {
        showToast({ type: "success", message: "Account deleted successfully." });
        setManageDeletionOpen(false);
        setSelectedUserForDeletion(null);

        setUsers((prevUsers) => prevUsers.filter(user => user.user_id !== userId));
      } else {
        showToast({
          type: "error",
          message: "Failed to delete account.",
          description: response.error || "Unknown error",
        });
      }
    } catch (error) {
      showToast({
        type: "error",
        message: "Error deleting account",
        description: error.message || error.toString(),
      });
    }
  };

  const handleConfirmPassword = async (password) => {
    try {
      const response = await dispatch(confirmAdminPassword(password));

      if (response.success) {
        showToast({ type: "success", message: "Password confirmed successfully." });
        setManageDeletionOpen(false);
        setSelectedUserForDeletion(null);
      } else {
        showToast({
          type: "error",
          message: "Password confirmation failed.",
          description: response.error || "Unknown error",
        });
      }

      return response;
    } catch (error) {
      showToast({
        type: "error",
        message: "Error confirming password",
        description: error.message || error.toString(),
      });

      return { success: false, error: error.message || "Unknown error" };
    }
  };


  const handleOpenArchiveUserDialog = (user) => {
    setSelectedUserForArchive(user);
    setArchiveUserDialogOpen(true);
  };

  const handleCloseArchiveUserDialog = () => {
    setSelectedUserForArchive(null);
    setArchiveUserDialogOpen(false);
  };

  const handleArchiveUser = () => {
    console.log("Archiving user:", selectedUserForArchive);
  };

  const handleOpenAddUserDrawer = () => setAddUserDrawerOpen(true);
  const handleCloseAddUserDrawer = () => setAddUserDrawerOpen(false);
  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setViewProfileDrawerOpen(true);
  };
  const handleCloseViewProfileDrawer = () => {
    setSelectedUser(null);
    setViewProfileDrawerOpen(false);
  };
  const handleOpenEditUserDrawer = (user) => {
    setSelectedUser(user);
    setEditUserDrawerOpen(true);
  };
  const handleCloseEditUserDrawer = () => {
    setSelectedUser(null);
    setEditUserDrawerOpen(false);
  };

  const handleManageDeletionRequest = (user) => {
    setSelectedUserForDeletion(user);
    setManageDeletionOpen(true);
  };

  const handleCloseManageDeletion = () => {
    setSelectedUserForDeletion(null);
    setManageDeletionOpen(false);
  };

  const generateMenuItems = (row) => {
    const user = row.original;

    const items = [
      {
        icon: <Contact size={16} />,
        text: "View Profile",
        onClick: () => handleViewProfile(user),
      },
      {
        icon: <Pencil size={16} />,
        text: "Edit Details",
        onClick: () => handleOpenEditUserDrawer(user),
      },
      {
        icon: <MessageSquare size={16} />,
        text: "Chat with User",
        onClick: () => history.push('/admin/messages'),
      },
    ];

    if (user.deletion_requested) {
      items.push({
        icon: <ShieldAlert size={16} color="red" />,
        text: "Manage Deletion Request",
        onClick: () => handleManageDeletionRequest(user),
      });
    }

    return items;
  };


  const statusColorScheme = {
    Active: "green",
    Inactive: "yellow",
    Suspended: "red",
  };

  const columnsData = [
    {
      Header: "Full Name",
      accessor: (row) => `${row.first_name} ${row.last_name}`,
    },
    { Header: "Phone Number", accessor: "phone_number" },
    { Header: "Type", accessor: "user_type" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge colorScheme={statusColorScheme[value]} borderRadius="full" px={2}>
          {value}
        </Badge>
      ),
    },
    { Header: "Region", accessor: "district" },
    {
      Header: "Deletion Request",
      accessor: (row) =>
        row.deletion_requested
          ? `Requested on ${formatDate(row.deletion_requested_at)}`
          : "None",
      Cell: ({ row }) =>
        row.original.deletion_requested ? (
          <Badge colorScheme="red" borderRadius="full" px={2}>
            Pending
          </Badge>
        ) : (
          <Badge colorScheme="green" borderRadius="full" px={2}>
            None
          </Badge>
        ),
    },

    {
      Header: "Date Added",
      accessor: "created_at",
      Cell: ({ value }) => formatDate(value),
    },
    {
      Header: "Actions",
      accessor: "moreOptions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Box p={6}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="md" fontWeight="bold">
            {loading ? (
              <Skeleton height="20px" width="100px" />
            ) : (
              `All Users: ${users?.length ?? 0}`
            )}
          </Text>

          {loading ? (
            <Skeleton height="40px" width="120px" />
          ) : (
            <GradientButton
              icon={<Plus size={16} />}
              onClick={handleOpenAddUserDrawer}
              height="40px"
            >
              Add User
            </GradientButton>
          )}
        </Flex>

      </Box>

      <Box height="70vh" overflowY="auto">
        <ReusableTable
          columnsData={columnsData}
          tableData={users}
          isLoading={loading}
          error={error}
        />

      </Box>

      <ReusableDrawer
        isOpen={isViewProfileDrawerOpen}
        onClose={handleCloseViewProfileDrawer}
        headerContent={<h2 style={{ fontSize: "md" }}>View Profile</h2>}
        bodyContent={
          selectedUser && (
            <ViewProfile
              user={selectedUser}
              onClose={handleCloseViewProfileDrawer}
            />
          )
        }
      />

      <EditUserDrawer
        isOpen={isEditUserDrawerOpen}
        onClose={handleCloseEditUserDrawer}
        user={selectedUser}
        onUpdateUser={handleUpdateUser}
      />

      <AddUserDrawer
        isOpen={isAddUserDrawerOpen}
        onClose={handleCloseAddUserDrawer}
        onAddUser={handleAddUser}
      />

      <ArchiveUser
        isOpen={isArchiveUserDialogOpen}
        onClose={handleCloseArchiveUserDialog}
        onConfirm={handleArchiveUser}
        firstName={selectedUserForArchive?.first_name || ""}
        lastName={selectedUserForArchive?.last_name || ""}
      />
      <ManageDeletionModal
        isOpen={isManageDeletionOpen}
        onClose={handleCloseManageDeletion}
        onConfirm={() => handleDeleteAccount(selectedUserForDeletion.user_id)}
        onConfirmPassword={handleConfirmPassword}
        firstName={selectedUserForDeletion?.first_name || ""}
        lastName={selectedUserForDeletion?.last_name || ""}
      />

    </Flex>
  );
};

export default UserManagement;
